/**
 * MarketplacePage — GAP-7: extension marketplace.
 *
 * Two surfaces:
 *   Browse  — full catalog with kind filter chips (Visual / Theme / Connector / Workflow)
 *   Installed — extensions the current tenant has installed, with toggle + uninstall
 *
 * Admins see install / uninstall buttons; non-admins see read-only cards.
 *
 * "Install" opens a config dialog driven by the extension's
 * `config_schema.fields`. Each field type maps onto a primitive input.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch, pickArray } from '@/lib/api';
import { getBrandName, useBrandName } from '@/lib/branding';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import * as LucideIcons from 'lucide-react';
import {
  Store, Search, Download, Trash2, BadgeCheck, Loader2, Settings, Power, Eye, X, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const KIND_LABEL = { visualization: 'Visual', theme: 'Theme', connector: 'Connector', workflow: 'Workflow' };
const KIND_TONE = {
  visualization: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  theme:         'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30',
  connector:     'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  workflow:      'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

function ExtIcon({ name, className = 'w-5 h-5' }) {
  // Look up the lucide-react icon dynamically; fall back to Store.
  const Icon = LucideIcons[name] || Store;
  return <Icon className={className} />;
}

function ExtensionCard({ ext, installed, isAdmin, onInstall, onConfigure, onUninstall, onToggle, onTrySample, onPreview }) {
  const canPreview = ext.kind === 'theme' || ext.kind === 'visualization';
  return (
    <div className="glass-card rounded-2xl p-5 hover:border-primary/30 transition-all" data-testid={`ext-${ext.extension_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
          <ExtIcon name={ext.icon} className="w-5 h-5 text-primary" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className={`text-[9px] ${KIND_TONE[ext.kind] || 'border-border/30'}`}>
            {KIND_LABEL[ext.kind] || ext.kind}
          </Badge>
          {ext.is_certified && (
            <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
              <BadgeCheck className="w-2.5 h-2.5 mr-0.5" /> Certified
            </Badge>
          )}
          {ext.is_featured && (
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/20" data-testid={`featured-badge-${ext.extension_id}`}>
              <Sparkles className="w-2.5 h-2.5 mr-0.5" /> Featured
            </Badge>
          )}
        </div>
      </div>
      <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{ext.name}</h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{ext.description}</p>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-3">
        <span>v{ext.version}</span>
        <span>·</span>
        <span>{ext.author}</span>
      </div>
      {installed ? (
        <div className="flex gap-1.5">
          {canPreview && (
            <Button onClick={() => onPreview(ext)} size="sm" variant="outline" className="rounded-lg h-7 text-xs" data-testid={`preview-${ext.extension_id}`}>
              <Eye className="w-3 h-3" />
            </Button>
          )}
          <Button onClick={() => onToggle(installed)} size="sm" variant={installed.enabled ? 'outline' : 'default'} className="flex-1 rounded-lg h-7 text-xs" data-testid={`toggle-${ext.extension_id}`}>
            <Power className="w-3 h-3 mr-1" /> {installed.enabled ? 'Enabled' : 'Disabled'}
          </Button>
          {isAdmin && (
            <>
              <Button onClick={() => onConfigure(ext, installed)} size="sm" variant="outline" className="rounded-lg h-7 text-xs" data-testid={`configure-${ext.extension_id}`}>
                <Settings className="w-3 h-3" />
              </Button>
              <Button onClick={() => onUninstall(installed)} size="sm" variant="outline" className="rounded-lg h-7 text-xs text-destructive" data-testid={`uninstall-${ext.extension_id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      ) : isAdmin ? (
        <div className="flex flex-col gap-1.5">
          {canPreview && (
            <Button onClick={() => onPreview(ext)} size="sm" variant="outline" className="w-full rounded-lg h-7 text-xs" data-testid={`preview-${ext.extension_id}`}>
              <Eye className="w-3 h-3 mr-1" /> Preview
            </Button>
          )}
          <Button onClick={() => onInstall(ext)} size="sm" className="w-full rounded-lg h-7 text-xs" data-testid={`install-${ext.extension_id}`}>
            <Download className="w-3 h-3 mr-1" /> Install
          </Button>
          {/* One-click sample wizard for the ClickHouse connector — installs
              the extension + adds a working sample data source + semantic
              model pointing at the public ClickHouse Playground. */}
          {ext.extension_id === 'mkt_clickhouse' && (
            <Button onClick={() => onTrySample?.(ext)} size="sm" variant="outline" className="w-full rounded-lg h-7 text-[10px]" data-testid="try-clickhouse-sample">
              ⚡ Try with sample data
            </Button>
          )}
        </div>
      ) : (
        canPreview ? (
          <Button onClick={() => onPreview(ext)} size="sm" variant="outline" className="w-full rounded-lg h-7 text-xs" data-testid={`preview-${ext.extension_id}`}>
            <Eye className="w-3 h-3 mr-1" /> Preview
          </Button>
        ) : (
          <div className="text-[10px] text-muted-foreground italic">Ask an admin to install</div>
        )
      )}
    </div>
  );
}

/**
 * PreviewDialog — visual preview of a marketplace theme or visualization
 * without installing. Themes render a swatch grid + sample chart; visuals
 * render an isolated preview using the extension's provided preview_url,
 * screenshot, or a generic placeholder.
 */
function PreviewDialog({ ext, onClose, onInstall, installed }) {
  if (!ext) return null;
  const isTheme = ext.kind === 'theme';
  const p = ext.preview || {};
  const palette = p.palette || ext.palette || [];
  const shot = p.screenshot_url || ext.screenshot_url;
  const embed = p.embed_url || ext.demo_url;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="marketplace-preview-modal">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-background border border-border/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
        <div className="px-5 py-3 border-b border-border/30 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold" data-testid="preview-name">{ext.name}</h3>
              <Badge variant="outline" className={`text-[9px] ${KIND_TONE[ext.kind] || 'border-border/30'}`}>{KIND_LABEL[ext.kind] || ext.kind}</Badge>
              {ext.is_certified && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20"><BadgeCheck className="w-2.5 h-2.5 mr-0.5" /> Certified</Badge>}
            </div>
            <p className="text-[10px] text-muted-foreground">v{ext.version} · {ext.author}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="preview-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          <p className="text-xs text-foreground/90">{ext.description}</p>

          {/* Theme preview: palette swatches + sample chart */}
          {isTheme && palette.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Colour palette</p>
              <div className="flex gap-1.5 flex-wrap">
                {palette.map((c, i) => (
                  <div key={i} className="flex flex-col items-center gap-1" data-testid={`preview-swatch-${i}`}>
                    <div className="w-10 h-10 rounded-lg border border-border/30" style={{ background: c }} />
                    <span className="text-[9px] font-mono text-muted-foreground/70">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isTheme && palette.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Sample bar chart</p>
              <div className="rounded-xl border border-border/30 p-4" style={{ background: p.background || '#0a0a0b' }}>
                <div className="flex items-end gap-2 h-32">
                  {[65, 82, 45, 92, 71, 58, 88].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t transition-all" style={{ height: `${h}%`, background: palette[i % palette.length] }} />
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-[9px]" style={{ color: p.foreground || '#94a3b8' }}>
                  {['Q1','Q2','Q3','Q4','Q1','Q2','Q3'].map((l, i) => <span key={i}>{l}</span>)}
                </div>
              </div>
            </div>
          )}

          {/* Screenshot preview (for visuals with a supplied image) */}
          {shot && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Preview</p>
              <img src={shot} alt={`${ext.name} preview`} className="w-full rounded-xl border border-border/30" data-testid="preview-screenshot" />
            </div>
          )}

          {/* Embed preview when the extension supplies a demo URL */}
          {embed && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Live demo</p>
              <iframe src={embed} title={`${ext.name} demo`} className="w-full h-72 rounded-xl border border-border/30 bg-black" data-testid="preview-embed" />
            </div>
          )}

          {/* Fallback when nothing to preview */}
          {!isTheme && !shot && !embed && (
            <div className="rounded-xl border border-dashed border-border/30 p-6 text-center">
              <ExtIcon name={ext.icon} className="w-8 h-8 text-muted-foreground/70 mx-auto mb-2" />
              <p className="text-[11px] text-muted-foreground">This extension does not include a visual preview.</p>
              <p className="text-[10px] text-muted-foreground/80 mt-1">Install to try it in the visual editor.</p>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border/30 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs" data-testid="preview-cancel">Close</Button>
          {!installed && (
            <Button size="sm" onClick={() => { onInstall(ext); onClose(); }} className="h-8 text-xs" data-testid="preview-install">
              <Download className="w-3 h-3 mr-1" /> Install
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfigField({ field, value, onChange }) {
  const t = field.type || 'string';
  if (t === 'select') {
    return (
      <Select value={value ?? field.default ?? ''} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs rounded-lg bg-muted/30 border-border/30"><SelectValue /></SelectTrigger>
        <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (t === 'number') {
    return <Input type="number" value={value ?? field.default ?? ''} onChange={e => onChange(Number(e.target.value))} className="h-8 text-xs rounded-lg bg-muted/30 border-border/30" />;
  }
  if (t === 'color') {
    return <Input type="color" value={value ?? field.default ?? '#000000'} onChange={e => onChange(e.target.value)} className="h-8 text-xs rounded-lg bg-muted/30 border-border/30" />;
  }
  if (t === 'password') {
    return <Input type="password" value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder="••••••••" className="h-8 text-xs rounded-lg bg-muted/30 border-border/30" />;
  }
  return <Input value={value ?? field.default ?? ''} onChange={e => onChange(e.target.value)} className="h-8 text-xs rounded-lg bg-muted/30 border-border/30" />;
}

function InstallDialog({ ext, existing, onClose, onSubmit }) {
  const [config, setConfig] = useState(existing?.config || {});
  const [busy, setBusy] = useState(false);
  if (!ext) return null;
  const fields = (ext.config_schema?.fields) || [];

  const handle = async () => {
    setBusy(true);
    try { await onSubmit(config); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!ext} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="install-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExtIcon name={ext.icon} className="w-4 h-4 text-primary" />
            {existing ? 'Configure' : 'Install'} — {ext.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-muted-foreground">{ext.description}</p>
          {fields.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No configuration required.</p>
          ) : (
            fields.map(f => (
              <div key={f.key}>
                <Label className="text-[10px] text-muted-foreground">{f.label}{f.required && <span className="text-destructive ml-1">*</span>}</Label>
                <ConfigField field={f} value={config[f.key]} onChange={v => setConfig(prev => ({ ...prev, [f.key]: v }))} />
              </div>
            ))
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={handle} disabled={busy} className="rounded-lg" data-testid="install-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            {existing ? 'Save' : 'Install'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SampleDatasetsBanner() {
  const [datasets, setDatasets] = useState([]);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    apiFetch(`${API}/api/marketplace/sample-datasets`, { method: 'GET' })
      .then(({ ok, data }) => { if (ok) setDatasets(data?.datasets || []); });
  }, []);
  const fire = async (ds) => {
    setBusyId(ds.id);
    try {
      const csrf = decodeURIComponent((document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1] || '');
      const { ok, data } = await apiFetch(`${API}${ds.setup_endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(ds.setup_body || {}),
      });
      if (!ok) throw new Error(data?.detail || 'Setup failed');
      const successMsg = data.message || `${ds.name} ready`;
      toast.success(successMsg);
      window.dispatchEvent(new Event('marketplace-changed'));
      if (data.redirect_to) {
        // The redirect is a full page load, so stash the success message
        // and re-toast it on the destination page for continuity.
        sessionStorage.setItem('sampleSetupToast', successMsg);
        setTimeout(() => { window.location.href = data.redirect_to; }, 600);
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };
  if (!datasets.length) return null;
  return (
    <div className="mb-4 p-4 rounded-2xl bg-primary/5 border border-primary/20" data-testid="sample-datasets-banner">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-base">⚡</span>
        <h3 className="text-sm font-semibold text-foreground">Try a sample dataset</h3>
        <span className="text-[10px] text-muted-foreground">Pre-configured one-click setups — perfect for trial walk-throughs.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {datasets.map(ds => (
          <button
            key={ds.id}
            onClick={() => fire(ds)}
            disabled={busyId === ds.id}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card/30 border border-border/30 text-left hover:bg-primary/10 hover:border-primary/30 transition-all disabled:opacity-50"
            data-testid={`sample-${ds.id}`}
          >
            <span className="text-[10px] text-primary font-mono w-9 text-center">{busyId === ds.id ? '...' : `${ds.estimated_seconds}s`}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">{ds.name}</p>
              <p className="text-[10px] text-muted-foreground line-clamp-1">{ds.description}</p>
              {(ds.rows_label || ds.engine) && (
                <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[9px] font-medium" data-testid={`sample-badge-${ds.id}`}>
                  {[ds.rows_label, ds.engine].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  const { user } = useAuth();
  const APP = useBrandName();
  const isAdmin = ['admin', 'tenant_admin', 'super_admin'].includes(user?.role || '');
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [installDialog, setInstallDialog] = useState(null); // { ext, existing }
  const [previewExt, setPreviewExt] = useState(null);
  const [tab, setTab] = useState('browse'); // 'browse' | 'installed'

  const csrf = () => decodeURIComponent((document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1] || '');
  const headers = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, inst] = await Promise.all([
        apiFetch(`${API}/api/marketplace`, { method: 'GET' }),
        apiFetch(`${API}/api/marketplace/installed`, { method: 'GET' }),
      ]);
      // White-label: the seed catalog authors items as "INZARA Labs" and some
      // descriptions mention INZARA. Swap the vendor name for the tenant's brand
      // across the user-facing text fields so it never leaks on cards.
      const brandText = (s) => (typeof s === 'string' ? s.replace(/INZARA/gi, getBrandName()) : s);
      const brandItems = (arr) => (arr || []).map(it => {
        if (!it || typeof it !== 'object') return it;
        const next = { ...it };
        for (const f of ['author', 'description', 'name', 'title', 'tagline', 'summary']) {
          if (typeof next[f] === 'string') next[f] = brandText(next[f]);
        }
        return next;
      });
      setCatalog(brandItems(cat.data?.items || []));
      setInstalled(brandItems(pickArray(inst, 'installed')));
      // Let ThemeContext (and any other extension consumer) refresh.
      window.dispatchEvent(new Event('marketplace-changed'));
    } catch (e) {
      toast.error(`Marketplace load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const installedByExtId = Object.fromEntries(installed.map(i => [i.extension_id, i]));

  const filtered = catalog.filter(e => {
    if (kindFilter !== 'all' && e.kind !== kindFilter) return false;
    if (searchQuery && !`${e.name} ${e.description}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const onInstall = (ext) => setInstallDialog({ ext, existing: null });
  const onConfigure = (ext, existing) => setInstallDialog({ ext, existing });

  const submitInstall = async (config) => {
    const ext = installDialog.ext;
    try {
      const { ok, data } = await apiFetch(`${API}/api/marketplace/${ext.extension_id}/install`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ config, enabled: true }),
      });
      if (!ok) throw new Error(data?.detail || 'Install failed');
      toast.success(`${ext.name} ${installDialog.existing ? 'updated' : 'installed'}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const onToggle = async (inst) => {
    try {
      const { ok, data } = await apiFetch(`${API}/api/marketplace/installed/${inst.install_id}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify({ enabled: !inst.enabled }),
      });
      if (!ok) throw new Error(data?.detail || 'Toggle failed');
      toast.success(data.enabled ? 'Enabled' : 'Disabled');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const onUninstall = async (inst) => {
    if (!window.confirm('Uninstall this extension? Configuration will be lost.')) return;
    try {
      const { ok, data } = await apiFetch(`${API}/api/marketplace/installed/${inst.install_id}`, {
        method: 'DELETE', headers: headers(),
      });
      if (!ok) throw new Error(data?.detail || 'Uninstall failed');
      toast.success('Uninstalled');
      load();
    } catch (e) { toast.error(e.message); }
  };

  // One-click sample wizard — installs ClickHouse + creates demo source + model.
  const onTrySample = async (ext) => {
    try {
      const { ok, data } = await apiFetch(`${API}/api/marketplace/clickhouse-sample/setup`, {
        method: 'POST', headers: headers(), body: '{}',
      });
      if (!ok) throw new Error(data?.detail || 'Setup failed');
      toast.success(`Sample created — opening Explore with "${data.model_id}"`);
      load();
      setTimeout(() => { window.location.href = data.redirect_to || '/ai-copilot'; }, 600);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketplace-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Store className="w-7 h-7 text-primary" /> Marketplace
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Extend {APP} with community + certified visualizations, themes, connectors, and workflows.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-border/30 rounded-xl p-0.5">
          {['browse', 'installed'].map(t => (
            <button key={t} onClick={() => setTab(t)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${tab === t ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                    data-testid={`tab-${t}`}>
              {t === 'browse' ? 'Browse' : `Installed (${installed.length})`}
            </button>
          ))}
        </div>
      </div>

      {tab === 'browse' && (
        <>
          {/* Sample Datasets gallery — one-click pre-configured setups */}
          <SampleDatasetsBanner />
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search extensions…" className="h-9 pl-9 text-xs rounded-lg bg-muted/30 border-border/30" data-testid="marketplace-search" />
            </div>
            <div className="flex items-center gap-1 border border-border/30 rounded-xl p-0.5">
              {[['all', 'All'], ['visualization', 'Visuals'], ['theme', 'Themes'], ['connector', 'Connectors'], ['workflow', 'Workflows']].map(([k, lbl]) => (
                <button key={k} onClick={() => setKindFilter(k)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${kindFilter === k ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                        data-testid={`kind-${k}`}>{lbl}</button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
          ) : (
            <>
              {/* Featured row — curated defaults pinned at the top so
                  new tenants find the good options first. Hidden when
                  the user has applied any filter/search so it doesn't
                  get in the way. */}
              {searchQuery === '' && (kindFilter === '' || kindFilter === 'all') && (() => {
                const featured = filtered
                  .filter(e => e.is_featured)
                  .sort((a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999));
                if (!featured.length) return null;
                return (
                  <div className="mb-6" data-testid="marketplace-featured-row">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <h3 className="text-sm font-semibold">Featured</h3>
                      <span className="text-[10px] text-muted-foreground">Curated by {APP}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {featured.map(ext => (
                        <ExtensionCard
                          key={`featured-${ext.extension_id}`}
                          ext={ext}
                          installed={installedByExtId[ext.extension_id]}
                          isAdmin={isAdmin}
                          onInstall={onInstall}
                          onPreview={setPreviewExt}
                          onConfigure={onConfigure}
                          onUninstall={onUninstall}
                          onToggle={onToggle}
                          onTrySample={onTrySample}
                        />
                      ))}
                    </div>
                    <div className="mt-4 mb-2 border-t border-border/20" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">All extensions</p>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(ext => (
                  <ExtensionCard
                    key={ext.extension_id}
                    ext={ext}
                    installed={installedByExtId[ext.extension_id]}
                    isAdmin={isAdmin}
                    onInstall={onInstall}
                    onPreview={setPreviewExt}
                    onConfigure={onConfigure}
                    onUninstall={onUninstall}
                    onToggle={onToggle}
                    onTrySample={onTrySample}
                  />
                ))}
                {filtered.length === 0 && (
                  <div className="col-span-full text-center py-12 text-sm text-muted-foreground">No extensions match your filter.</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'installed' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
          ) : installed.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center text-sm text-muted-foreground">
              No extensions installed yet.{isAdmin && ' Visit the Browse tab to install one.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {installed.map(inst => inst.extension && (
                <ExtensionCard
                  key={inst.install_id}
                  ext={inst.extension}
                  installed={inst}
                  isAdmin={isAdmin}
                  onInstall={onInstall}
                  onPreview={setPreviewExt}
                  onConfigure={onConfigure}
                  onUninstall={onUninstall}
                  onToggle={onToggle}
                />
              ))}
            </div>
          )}
        </>
      )}

      <InstallDialog
        ext={installDialog?.ext}
        existing={installDialog?.existing}
        onClose={() => setInstallDialog(null)}
        onSubmit={submitInstall}
      />
      {previewExt && (
        <PreviewDialog
          ext={previewExt}
          installed={installed.find(i => i.extension_id === previewExt.extension_id)}
          onClose={() => setPreviewExt(null)}
          onInstall={onInstall}
        />
      )}
    </div>
  );
}
